/**
 * R18-7.1 — Overlay tracking context.
 *
 * Maintains a Set<string> of currently mounted blocking overlays so the
 * subscription discipline layer in Room.jsx can force-unsubscribe non-pinned
 * video while any overlay is visually covering the room.
 *
 * USAGE
 *   const { registerOverlay, unregisterOverlay, overlayCount } = useOverlay();
 *   useEffect(() => {
 *     if (!visible) return undefined;
 *     registerOverlay("my-modal");
 *     return () => unregisterOverlay("my-modal");
 *   }, [visible]);
 *
 * Side-tray panels (HostApprovalTray, CameraRequestsTray) DO NOT register —
 * they only cover the edge of the screen and shouldn't trigger an
 * unsubscribe sweep.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const OverlayContext = createContext({
  registerOverlay: () => {},
  unregisterOverlay: () => {},
  overlayCount: 0,
});

export function OverlayProvider({ children }) {
  const [overlayCount, setOverlayCount] = useState(0);
  // Ref source-of-truth so register/unregister are stable across renders.
  const setRef = useRef(new Set());

  const registerOverlay = useCallback((id) => {
    if (!id || setRef.current.has(id)) return;
    setRef.current.add(id);
    const c = setRef.current.size;
    // eslint-disable-next-line no-console
    console.log(`[R18-7.1] overlay change count=${c} (+${id})`);
    setOverlayCount(c);
  }, []);

  const unregisterOverlay = useCallback((id) => {
    if (!id || !setRef.current.has(id)) return;
    setRef.current.delete(id);
    const c = setRef.current.size;
    // eslint-disable-next-line no-console
    console.log(`[R18-7.1] overlay change count=${c} (-${id})`);
    setOverlayCount(c);
  }, []);

  const value = useMemo(
    () => ({ registerOverlay, unregisterOverlay, overlayCount }),
    [registerOverlay, unregisterOverlay, overlayCount],
  );
  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
}

export function useOverlay() {
  return useContext(OverlayContext);
}

/** Convenience hook — register/unregister an id while `visible` is truthy. */
export function useRegisterOverlay(id, visible) {
  const { registerOverlay, unregisterOverlay } = useOverlay();
  React.useEffect(() => {
    if (!visible || !id) return undefined;
    registerOverlay(id);
    return () => unregisterOverlay(id);
  }, [id, visible, registerOverlay, unregisterOverlay]);
}
